import { useCallback, useEffect, useMemo, useState } from 'react';
import { MusicAPIIntegrationID } from 'fym-shared/src/music-services/ids';
import { useLazySearchQuery } from '../../../../redux/api/musicAPI';
import { useDebouncedValue } from '../../../smartLinks/catalog/useDebouncedValue';
import { FIND_IT_DEBOUNCE_MS, findItPrefill, FindItResult, findItResults, findItSearchOptions } from '../findItSearch';

export type FindItStatus = 'loading' | 'done' | 'failed';

export interface FindItSearch {
  query: string;
  setQuery: (query: string) => void;
  status: FindItStatus;
  results: FindItResult[];
  retry: () => void;
}

/**
 * The search of "Find it" (design d4): the MusicAPI search of the claim flow, one service, tracks
 * only. The field starts with "<artist> <title>"; a change searches again after 400 ms.
 */
export function useFindItSearch({
  integrationType,
  song,
  enabled,
}: {
  integrationType: MusicAPIIntegrationID;
  song: { title: string; artistName: string };
  enabled: boolean;
}): FindItSearch {
  const [query, setQuery] = useState(() => findItPrefill(song.title, song.artistName));
  const debounced = useDebouncedValue(query, FIND_IT_DEBOUNCE_MS);
  const [search, { data, isFetching, isError }] = useLazySearchQuery();
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!enabled || debounced.trim() === '') {
      return;
    }
    search(findItSearchOptions(integrationType, debounced, song)).catch(console.error);
    // `song` is read at the call: a new song is a new sheet.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt, debounced, enabled, integrationType, search]);

  const results = useMemo(() => findItResults(data, integrationType), [data, integrationType]);
  const retry = useCallback(() => setAttempt((count) => count + 1), []);
  // Before the first answer the sheet shows the loading rows, not "No match".
  const isWaiting = isFetching || query !== debounced || (data === undefined && !isError);
  let status: FindItStatus = 'done';
  if (isWaiting) {
    status = 'loading';
  } else if (isError) {
    status = 'failed';
  }
  return { query, setQuery, status, results, retry };
}
